import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle } from "@phosphor-icons/react";

export default function AccountSettingsPage() {
    const { user } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) { setName(user.name || ""); setEmail(user.email || ""); }
    }, [user]);

    async function saveProfile(e) {
        e.preventDefault();
        setError(""); setSuccess(""); setSaving(true);
        try {
            await http.patch("/auth/account", { name, email });
            setSuccess("Profile updated.");
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    }

    async function savePassword(e) {
        e.preventDefault();
        setError(""); setSuccess("");
        if (newPassword !== confirmPassword) { setError("New passwords don't match."); return; }
        if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
        setSaving(true);
        try {
            await http.patch("/auth/account", {
                current_password: currentPassword,
                new_password: newPassword,
            });
            setSuccess("Password updated.");
            setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    }

    return (
        <div data-testid="account-settings-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-12">
            <div className="label-eyebrow mb-3">/ Account</div>
            <h1 className="font-serif text-4xl sm:text-5xl mb-10">Your <span className="italic gold-text">settings</span></h1>

            {success && (
                <div data-testid="settings-success" className="mb-6 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-sm px-4 py-3 flex items-center gap-2">
                    <CheckCircle size={18} weight="fill" /> {success}
                </div>
            )}
            {error && <div data-testid="settings-error" className="mb-6 text-red-400 text-sm">{error}</div>}

            <section className="bg-slate-900/50 border border-white/5 rounded-sm p-6 sm:p-8 mb-6">
                <h2 className="font-serif text-2xl mb-6">Profile</h2>
                <form onSubmit={saveProfile} className="space-y-5">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-slate-400">Display name</label>
                        <input
                            data-testid="settings-name"
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-slate-400">Email</label>
                        <input
                            data-testid="settings-email"
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                        />
                        <p className="text-xs text-slate-500 mt-2">Used for sign-in and password reset emails.</p>
                    </div>
                    <button data-testid="settings-save-profile" disabled={saving} className="btn-gold disabled:opacity-50">
                        {saving ? "Saving…" : "Save profile"}
                    </button>
                </form>
            </section>

            <section className="bg-slate-900/50 border border-white/5 rounded-sm p-6 sm:p-8">
                <h2 className="font-serif text-2xl mb-6">Change password</h2>
                <form onSubmit={savePassword} className="space-y-5">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-slate-400">Current password</label>
                        <input
                            data-testid="settings-current-password"
                            type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                            className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-slate-400">New password</label>
                            <input
                                data-testid="settings-new-password"
                                type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-slate-400">Confirm new password</label>
                            <input
                                data-testid="settings-confirm-password"
                                type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
                            />
                        </div>
                    </div>
                    <button data-testid="settings-save-password" disabled={saving} className="btn-gold disabled:opacity-50">
                        {saving ? "Updating…" : "Update password"}
                    </button>
                </form>
            </section>
        </div>
    );
}
